import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { debounce, isEmpty } from 'lodash';

import { useTranslation } from 'react-i18next';
import APortal from '../../UI/APortal/APortal.js';
import BigNumber from '../../UI/BigNumber/BigNumber';
import BigMonetary from '../../UI/BigMonetary/BigMonetary';
import ProgressBar from '../../UI/ProgressBar/ProgressBar';
import { isValidColor, lighten } from '../../Utils/colorManipulator';
import { getHost } from '../../../helper/getHost';
import MiniChartSVG from '../../UI/MiniChartSVG/MiniChartSVG';
import ObjFieldCamRec from '../../UI/Objects/ObjFieldCamRec/ObjFieldCamRec.js';
import ObjFieldDrawingPad from '../../UI/Objects/ObjFieldDrawingPad/ObjFieldDrawingPad';
import ObjFieldFormula from '../../UI/Objects/ObjFields/ObjFieldFormula';
import ObjFieldDate from '../../UI/Objects/ObjFields/ObjFieldDate.js';
import ObjFieldList from '../../UI/Objects/ObjFields/ObjFieldList.js';
import ObjFieldListMulti from '../../UI/Objects/ObjFields/objFieldListMulti.js';
import ObjectSelector from '../../object-selector/index.js';
import {
  removeTagHtml,
  formatNumber,
  formatMonetary,
  getTypeOfField,
  getFieldDef,
} from '../../Utils/Utils';
import {
  DivSmallAvatarStyle,
  DivSmallTagAreaStyle,
  DivSmallTagNumberStyle,
  DivSmallTagStyle,
  FieldCamrecContent,
  FieldFile,
  FieldFileContainer,
  FieldListContent,
  HalfBlocSmallTag,
  HalfBlocSmallTagLabelStyle,
  NeutralDiv,
  PercentFieldContent,
  SpanSmallAvatarLabelStyle,
  SpanSmallAvatarValueStyle,
  SpanSmallTagAreaValueStyle,
  SpanSmallTagLabelStyle,
  SpanSmallTagValueStyle,
  TextFieldUrlContent,
} from './SmallTagsCSS.js';
import { parseJson } from '../../../utils/parseJson.js';
import ObjectActionCreators from '../../../actions.js';
import TiptapEditor from '../../UI/Objects/ObjFields/TiptapEditor';
import { Avatar } from '../../UI/Avatar/Avatar.js';
import { removeQuote } from '../../Utils/TextUtils.js';
import ReduxDrawingPadWrapper from '../../UI/Objects/ObjFieldDrawingPad/ReduxDrawingPadWrapper.js';
import ObjFieldCrumbTrail from '../../UI/Objects/ObjFields/ObjFieldCrumbTrail.js';
import { useFieldDef } from '../../../hooks/useFieldDef.js';
import { useFieldValueResolver } from '../../../hooks/useFieldValueResolver.js';
import { useFieldListResolver } from '../../../hooks/useFieldListResolver.js';

const SHOW_AREA_LABEL = false;
export default function SmallTag({
  field,
  ind,
  nodeColor,
  pal,
  onLoad,
  objectsDefList,
  zoneType,
  objId,
  node,
}) {
  const lockKey = `${objId}:${field.attrName}`;
  const DBG = process.env.NODE_ENV !== 'production'; // or just true while testing

  // Pretty logger with namespaces
  const log = (...a) => DBG && console.log('%c[SmallTag]', 'color:#09f', ...a);
  const warn = (...a) =>
    DBG && console.warn('%c[SmallTag]', 'color:#fa0', ...a);
  const group = (title, fn) => {
    if (!DBG) return fn?.();
    console.groupCollapsed(`%c[SmallTag] ${title}`, 'color:#09f');
    try {
      fn?.();
    } finally {
      console.groupEnd();
    }
  };

  // Render counter + field identity
  const renderRef = useRef(0);
  renderRef.current += 1;
  const fieldKey = `${objId}:${field.type}:${field.attrName}`;
  // log('render#', renderRef.current, { fieldKey });

  let res = '';
  let data = field.value;

  // 1️⃣ Résolution des métadonnées du champ
  const fieldDef = useFieldDef({
    objectType: field.type,
    fieldName: field.attrName,
    node,
    value: field.value,
  });

  // 2️⃣ Résolution des valeurs d'affichage
  const fieldValue = useFieldValueResolver({
    fieldDef,
    node,
  });

  // 3️⃣ Résolution des options de liste (si applicable)
  const fieldList = useFieldListResolver({
    fieldDef,
    singleValue:
      fieldDef.type === 'list' && !fieldDef.isMultiple
        ? fieldValue.resolvedValue
        : null,
    values:
      fieldDef.type === 'list' && fieldDef.isMultiple
        ? fieldValue.multiValues
        : [],
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(data);

  // useEffect(() => {
  //   log('fieldDef▲', {
  //     type: fieldDef.type,
  //     name: fieldDef.fieldName,
  //     isMultiple: fieldDef.isMultiple,
  //     canWrite: fieldDef.canWrite,
  //     formulaCat1: fieldDef.formulaCat1,
  //     formulaCat2: fieldDef.formulaCat2,
  //   });
  // }, [
  //   fieldDef.type,
  //   fieldDef.fieldName,
  //   fieldDef.isMultiple,
  //   fieldDef.canWrite,
  //   fieldDef.formulaCat1,
  //   fieldDef.formulaCat2,
  // ]);

  // useEffect(() => {
  //   log('fieldValue▲', {
  //     raw: fieldValue.rawValue,
  //     display: fieldValue.displayValue,
  //     multi: fieldValue.multiValues,
  //   });
  // }, [fieldValue.rawValue, fieldValue.displayValue, fieldValue.multiValues]);

  const isMulti = fieldDef.isMultiple;
  const canWrite = fieldDef.canWrite;
  const dispatch = useDispatch();
  // let canWrite = true; // XXX todo

  const typeOfField = useMemo(
    () => getTypeOfField(objectsDefList, field.type, field.attrName),
    [objectsDefList, field.type, field.attrName]
  );

  const handleBlur = (e) => {
    setEditValue(e.target.value);
    setIsEditing(false);
  };

  const handleChangeDate = (name, date) => {
    setEditValue(date);
    setIsEditing(false);
  };
  const changeValue = (newValue) => {
    setIsEditing(false);
  };

  const handlePreventInputEvent = (e) => {
    e.stopPropagation();
    // e.preventDefault(); remving default behavior would impact +/- ui on input field
  };

  const debouncedSave = useMemo(
    () =>
      debounce((objFormData) => {
        dispatch(ObjectActionCreators.saveObjectFormData(objId, [objFormData]));
      }, 500),
    [dispatch, objId]
  );
  useEffect(() => () => debouncedSave.cancel(), [debouncedSave]);

  useEffect(() => {
    if (editValue !== data) {
      let objFormData = {
        type: field.type,
        key: objId,
        attrName: isMulti ? `${field.attrName}_multval` : field.attrName,
        value: editValue,
        defaultValue: editValue,
      };
      if (canWrite) {
        debouncedSave(objFormData);
      }
    }
  }, [editValue]);

  const objFormData = useMemo(
    () => getObjectDefFromType(field.type, objectsDefList)?.objFormData,
    [objectsDefList, field.type]
  );

  if (objFormData) {
    let type = fieldDef.type;
    let label = fieldDef.label;
    let option = fieldDef.options;
    let unit = fieldDef.unit;
    let formulaObject = fieldDef.formulaObject;
    let formulaCat1 = fieldDef.formulaCat1;
    let formulaCat2 = fieldDef.formulaCat2;
    let value = fieldValue.rawValue;

    if (zoneType === 'zoneStandard') {
      switch (type) {
        case 'area':
          if (field.attrName === 'crumbtrail') {
            res = _fieldCrumbTrail(
              node.data.crumbtrail,
              node.data.crumbtrailIds
            );
          } else {
            res =
              canWrite && isEditing ? (
                <div id={`portalArea${objId}`}>
                  <APortal
                    idParent={`portalArea${objId}`}
                    onClose={() => setIsEditing(false)}
                    width={600}
                    height={400}
                  >
                    <TiptapEditor
                      value={data}
                      onUpdate={(newValue) => setEditValue(newValue)}
                      editable={true}
                    />
                  </APortal>
                </div>
              ) : (
                <div
                  onMouseUp={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    if (canWrite) setIsEditing(true);
                  }}
                >
                  {_fieldArea(ind, label, value)}
                </div>
              );
          }
          break;
        case 'date':
          res = isEditing ? (
            <div id={`portaldate${objId}`}>
              <APortal
                idParent={`portaldate${objId}`}
                onClose={() => setIsEditing(false)}
              >
                <ObjFieldDate
                  isEditing={isEditing}
                  readOnly={!canWrite}
                  handleChangeFieldValue={handleChangeDate}
                  value={data}
                  options={option}
                />
              </APortal>
            </div>
          ) : (
            <div
              onMouseUp={(e) => e.stopPropagation()}
              onClick={(e) => {
                if (canWrite) setIsEditing(true);
              }}
            >
              {_fieldDate(ind, label, value)}
            </div>
          );

          break;
        case 'text':
          res =
            canWrite && isEditing ? (
              <input
                onMouseUp={handlePreventInputEvent}
                type="text"
                value={isEditing ? editValue : fieldValue.rawValue ?? ''}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={changeValue}
                autoFocus
                style={{ width: '100%', height: '100%', fontSize: 12 }}
              />
            ) : (
              <div
                onMouseUp={(e) => e.stopPropagation()}
                onClick={(e) => {
                  if (canWrite) setIsEditing(true);
                }}
              >
                {_fieldText(
                  ind,
                  label,
                  value,
                  canWrite,
                  fieldValue?.ui?.fg,
                  fieldValue?.ui?.bg
                )}
              </div>
            );

          break;
        case 'number':
          switch (fieldDef.options) {
            case 'progressbar':
              res = isNaN(+data) ? (
                <div></div>
              ) : canWrite && isEditing ? (
                <input
                  type="number"
                  defaultValue={data}
                  onMouseUp={handlePreventInputEvent}
                  onBlur={handleBlur}
                  style={{ width: '100%', height: '100%', fontSize: 12 }}
                  autoFocus
                />
              ) : (
                <div
                  onMouseUp={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    if (canWrite) setIsEditing(true);
                  }}
                >
                  {_progessBarNumber(
                    ind,
                    label,
                    value,
                    pal,
                    nodeColor,
                    field.type,
                    field.attrName
                  )}
                </div>
              );

              break;
            case 'bignumber':
              break;
            case 'bigmonetary':
              break;
            default:
              res = isNaN(+data) ? (
                <div></div>
              ) : canWrite && isEditing ? (
                <input
                  type="number"
                  defaultValue={data}
                  onMouseUp={handlePreventInputEvent}
                  onBlur={handleBlur}
                  style={{ width: '100%', height: '100%', fontSize: 12 }}
                  autoFocus
                />
              ) : (
                <div
                  onMouseUp={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    if (canWrite) setIsEditing(true);
                  }}
                >
                  {_fieldNumber(
                    ind,
                    label,
                    value,
                    unit,
                    fieldValue.ui.fg,
                    fieldValue.ui.bg
                  )}
                </div>
              );
              break;
          }

          break;
        case 'camrec':
          res = _fieldCamrec(ind, label, value, pal, field);
          break;
        case 'file':
          res = _fieldFile(ind, label, value, field, onLoad);
          break;
        case 'drawing':
          res = _fieldDrawing(ind, label, value, field);

          break;
        case 'list':
          break;
        case 'formula':
          switch (fieldDef.options) {
            case 'bignumber':
              break;
            case 'bigMonetary':
              break;
            default:
              res = _fieldformula(
                objId,
                ind,
                label,
                value,
                unit,
                pal,
                fieldDef,
                field.type,
                field.attrName,
                formulaObject,
                formulaCat1,
                formulaCat2,
                option,
                fieldDef.optionsCat1,
                fieldDef.optionsCat2,
                fieldValue.ui.fg,
                fieldValue.ui.bg
              );
              break;
          }
          break;
        default:
          if (fieldDef.isLinked) {
            res = isEditing ? (
              <div id={`portallink${objId}`}>
                <APortal
                  idParent={`portallink${objId}`}
                  onClose={() => setIsEditing(false)}
                >
                  <ObjectSelector
                    typeSelectedObject={typeOfField}
                    onSelectValue={(value) => {
                      setEditValue(value);
                      setIsEditing(false);
                    }}
                    isDisplaySummary={false}
                    isDisplayTitle={false}
                    isDisplayConfirm={false}
                    height={400}
                  />
                </APortal>
              </div>
            ) : (
              <div
                onMouseUp={(e) => e.stopPropagation()}
                onClick={(e) => {
                  if (canWrite) setIsEditing(true);
                }}
              >
                {String(fieldValue.rawValue || '').startsWith('emplo')
                  ? _fieldAvatar(
                      `${ind}-${fieldValue.rawValue}`,
                      label,
                      fieldValue.displayValue,
                      fieldValue.rawValue
                    )
                  : _fieldDefault(
                      `${ind}-${fieldValue.rawValue}`,
                      label,
                      fieldValue.displayValue,
                      fieldValue.ui.fg,
                      fieldValue.ui.bg
                    )}
              </div>
            );
          } else {
            res = _fieldDefault(
              `${ind}-${fieldValue.rawValue}`,
              label,
              fieldValue.displayValue,
              fieldValue.ui.fg,
              fieldValue.ui.bg
            );
          }

          break;
      }
    } else if (zoneType === 'zoneBigNumber') {
      switch (type) {
        case 'number':
          switch (fieldDef.options) {
            case 'percent':
              res = isNaN(+data) ? (
                <div></div>
              ) : canWrite && isEditing ? (
                <input
                  type="number"
                  defaultValue={data}
                  onMouseUp={handlePreventInputEvent}
                  onBlur={handleBlur}
                  style={{ width: '100%', height: '100%', fontSize: 12 }}
                  autoFocus
                />
              ) : (
                <NeutralDiv
                  onMouseUp={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    if (canWrite) setIsEditing(true);
                  }}
                >
                  {_percentNumber(ind, label, value, nodeColor, unit)}
                </NeutralDiv>
              );

              break;
            case 'bignumber':
              res = isNaN(+data) ? (
                <div></div>
              ) : canWrite && isEditing ? (
                <input
                  type="number"
                  defaultValue={data}
                  onMouseUp={handlePreventInputEvent}
                  onBlur={handleBlur}
                  style={{ width: '100%', height: '100%', fontSize: 12 }}
                  autoFocus
                />
              ) : (
                <NeutralDiv
                  onMouseUp={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    if (canWrite) setIsEditing(true);
                  }}
                >
                  {_bigNumber(
                    ind,
                    label,
                    isNaN(+value) ? '' : formatNumber(value),
                    fieldValue?.ui?.fg || nodeColor,
                    unit
                  )}
                </NeutralDiv>
              );
              break;
            case 'bigmonetary':
              res = isNaN(+data) ? (
                <div></div>
              ) : canWrite && isEditing ? (
                <input
                  type="number"
                  defaultValue={data}
                  onMouseUp={handlePreventInputEvent}
                  onBlur={handleBlur}
                  style={{ width: '100%', height: '100%', fontSize: 12 }}
                  autoFocus
                />
              ) : (
                <NeutralDiv
                  onMouseUp={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    if (canWrite) setIsEditing(true);
                  }}
                >
                  {_bigNumber(
                    ind,
                    label,
                    formatMonetary(value),
                    fieldValue?.ui?.fg || nodeColor,
                    unit
                  )}
                </NeutralDiv>
              );
              break;
            default:
              break;
          }

          break;
        case 'formula':
          switch (fieldDef.options) {
            case 'bignumber':
              res = _bigNumber(
                ind,
                label,
                isNaN(+value) ? '' : formatNumber(value),
                fieldValue?.ui?.fg || nodeColor,
                unit
              );

              break;
            case 'bigmonetary':
              res = _bigMonetary(
                ind,
                label,
                isNaN(+value) ? '' : formatMonetary(value),
                fieldValue?.ui?.fg || nodeColor,
                unit
              );
              break;
            default:
              break;
          }
          break;
        default:
          break;
      }
    } else if (zoneType === 'zoneBadge') {
      switch (type) {
        case 'list':
          const isMulti = fieldDef.isMultiple;
          // valeur multiple: string[] stockée sous attrName+'_mulvVal' dans node.data
          let multValsRaw = isMulti
            ? node?.data?.[`${field.attrName}_multval`]
            : null;
          let multVals = Array.isArray(multValsRaw)
            ? multValsRaw
            : parseJson(multValsRaw) || [];

          res = isEditing ? (
            <div id={`portalliste${objId}`}>
              <APortal
                idParent={`portalliste${objId}`}
                onClose={() => setIsEditing(false)}
              >
                {isMulti ? (
                  <ObjFieldListMulti
                    key={`listmulti-${ind}`}
                    keyParam={objId}
                    attrName={field.attrName}
                    readOnly={false}
                    options={option}
                    values={multVals}
                    unit={field.unit}
                    isModal={true}
                    onChangeValues={(vals) => {
                      // on pousse l'ensemble d'un coup (même clé que d’habitude)
                      handleChangeDate(`${field.attrName}_multval`, vals);
                    }}
                  />
                ) : (
                  <ObjFieldList
                    isEditing={isEditing}
                    readOnly={!canWrite}
                    handleChangeFieldValue={handleChangeDate}
                    value={data}
                    options={option}
                    unit={unit}
                    orientation="vertical"
                  />
                )}
              </APortal>
            </div>
          ) : (
            <NeutralDiv
              onMouseUp={(e) => e.stopPropagation()}
              onClick={(e) => {
                if (canWrite) setIsEditing(true);
              }}
            >
              {isMulti && multVals?.length
                ? _fieldListMulti(fieldDef, fieldList, ind)
                : _fieldList(fieldDef, fieldList, ind)}
            </NeutralDiv>
          );
          break;

        default:
          break;
      }
    }
  }

  return res;
}

//------------------------------------------------------------------------------
export const _fieldCrumbTrail = (crumbtrail, crumbtrailIds) =>
  crumbtrail && crumbtrailIds ? (
    <ObjFieldCrumbTrail crumbTrail={crumbtrail} crumbtrailIds={crumbtrailIds} />
  ) : null;

//------------------------------------------------------------------------------
export const _fieldArea = (ind, label, value, canWrite = false) =>
  value ? (
    <AreaField key={ind + label} label={label} value={value}></AreaField>
  ) : null;
//------------------------------------------------------------------------------
export const _fieldDate = (ind, label, value, canWrite = false) => {
  let aDate = new Date(parseInt(value));
  if (aDate.toLocaleDateString() === 'Invalid Date') {
    aDate = '-';
  } else {
    aDate = aDate.toLocaleDateString();
  }
  return <TextField key={ind + label} label={label} value={aDate}></TextField>;
};
//------------------------------------------------------------------------------
export const _fieldText = (ind, label, value, canWrite = false, fg, bg) => (
  <TextField
    key={ind + label}
    label={label}
    value={value}
    canWrite={canWrite}
    fg={fg}
    bg={bg}
  ></TextField>
);
//------------------------------------------------------------------------------
export const _fieldNumber = (
  ind,
  label,
  value = '',
  unit = '',
  canWrite = false,
  fg,
  bg
) => (
  <NumberField
    key={ind + label}
    label={label}
    value={value}
    unit={unit}
    fg={fg}
    bg={bg}
  ></NumberField>
);
//------------------------------------------------------------------------------
export const _bigNumber = (
  ind,
  label,
  value,
  nodeColor,
  unit = '',
  canWrite = false
) => (
  <BigNumberField
    key={ind + label}
    label={label}
    value={value}
    unit={unit}
    nodeColor={nodeColor}
  ></BigNumberField>
);
//------------------------------------------------------------------------------
export const _bigMonetary = (
  ind,
  label,
  value,
  nodeColor,
  unit = '',
  canWrite = false
) => (
  <BigMonetaryField
    key={ind + label}
    label={label}
    value={value}
    unit={unit}
    nodeColor={nodeColor}
  ></BigMonetaryField>
);
//------------------------------------------------------------------------------
export const _percentNumber = (
  ind,
  label,
  value,
  nodeColor,
  unit = '',
  canWrite = false
) => (
  <PercentField
    key={ind + label}
    label={label}
    value={value}
    unit={unit}
    nodeColor={nodeColor}
  ></PercentField>
);
//------------------------------------------------------------------------------

//------------------------------------------------------------------------------
export const _fieldCamrec = (
  ind,
  label,
  value,
  pal,
  field,
  canWrite = false
) => {
  let res = value != null && value.length > 0 && (
    <FieldCamrecContent key={ind + label}>
      <ObjFieldCamRec
        mini={true}
        readOnly={true}
        keyParam={field.keyParam}
        value={value + ind}
        pal={pal}
      />
    </FieldCamrecContent>
  );
  return res;
};
//------------------------------------------------------------------------------

export const _fieldFile = (
  ind,
  label,
  value,
  field,
  onLoad,
  canWrite = false
) => {
  return (
    <FieldFileContainer key={ind + label}>
      <FieldFile
        alt=""
        onLoad={onLoad}
        src={`https://${getHost()}/guerillaPM3/DownloadFile/download/${value}/${
          field.fileOrigName
        }`}
      />
    </FieldFileContainer>
  );
};
//------------------------------------------------------------------------------
export const _fieldDrawing = (ind, label, value, field, canWrite = false) => {
  return (
    <div key={ind + label}>
      <ReduxDrawingPadWrapper
        readOnly={true}
        keyParam={field.keyParam}
        value={value}
      />
    </div>
  );
};
//------------------------------------------------------------------------------
export const _fieldListMulti = (fieldDef, fieldList, ind) => {
  try {
    return (
      <>
        {fieldList.selectedList.map((optionCurr, i) => {
          let nodeColor = optionCurr.color ?? 'var(--ds-base)';
          let nodeColorLight = isValidColor(nodeColor)
            ? lighten(nodeColor, 0.9)
            : 'var(--ds-bg-secondary)';

          return (
            <FieldListContent
              key={`${ind}${optionCurr.value}-${optionCurr.value}-${i}`}
              $backgroundColor={nodeColorLight}
              title={optionCurr.display}
            >
              {optionCurr?.icon && (
                <div
                  style={{
                    position: 'relative',
                    display: 'inline',
                    width: '16px',
                    height: '16px',
                    backgroundColor: optionCurr.color,
                    mask: `url('${optionCurr.icon}')`,
                    WebkitMask: `url('${optionCurr.icon}')`,
                    maskRepeat: 'no-repeat',
                    WebkitMaskRepeat: 'no-repeat',
                    maskSize: '16px 16px',
                    WebkitMaskSize: '16px 16px',
                    opacity: 0.7,
                    color: 'transparent',
                    marginRight: 4,
                  }}
                />
              )}
              {optionCurr?.display}
              {fieldDef.unit}
            </FieldListContent>
          );
        })}
      </>
    );
  } catch (err) {
    console.error('_fieldListMulti error:', err);
    return null;
  }
};
export const _fieldList = (fieldDef, fieldList, ind) => {
  try {
    let label = fieldDef.label;
    let option = fieldList.selected;
    let nodeColor = option?.color ?? 'var(--ds-base)';
    let nodeColorLight = isValidColor(nodeColor)
      ? lighten(nodeColor, 0.9)
      : 'var(--ds-bg-secondary)';

    let res = option?.display ? (
      <>
        <FieldListContent
          key={ind + label}
          $backgroundColor={nodeColorLight}
          title={fieldDef.label}
        >
          {option?.icon && (
            <div
              key={`icon-hd-${option?.icon}-${nodeColor}`}
              style={{
                position: 'relative',
                display: 'inline',
                width: '16px',
                height: '16px',
                backgroundColor: nodeColor,
                mask: `url('${option?.icon ?? '/img/alert.svg'}')`,
                maskRepeat: 'no-repeat',
                maskSize: '16px 16px',
                right: '5px',
                top: '1px',
                opacity: '0.7',
                color: 'transparent',
              }}
            >
              .
            </div>
          )}
          {option?.display}
          {fieldDef.unit}
        </FieldListContent>
      </>
    ) : (
      <></>
    );

    return res;
  } catch (error) {}
};
//------------------------------------------------------------------------------
export const _progessBarNumber = (
  ind,
  label,
  value,
  pal,
  nodeColor,
  type,
  attrName,
  canWrite = false
) => (
  <ObjFieldFormula
    mini={true}
    key={ind + label}
    value={JSON.stringify([{ result: value, status: label }])}
    pal={pal}
    options={JSON.stringify([{ type: 'percent' }])}
    type={type}
    attrName={attrName}
  ></ObjFieldFormula>
);

export const _fieldformula = (
  objId,
  ind,
  label,
  value,
  unit = '',
  pal,
  fldObj,
  type,
  attrName,
  formulaObject,
  f1,
  f2,
  option,
  optionsCat1,
  optionsCat2,
  fg,
  bg
) => {
  let res;
  if (value.startsWith('[') && value.length > 2) {
    res = (
      <ObjFieldFormula
        key={ind + label}
        objId={objId}
        mini={true}
        formulaObject={formulaObject}
        formulaCat1={f1}
        formulaCat2={f2}
        optionsCat1={optionsCat1}
        optionsCat2={optionsCat2}
        value={value}
        pal={pal}
        options={option || fldObj.options}
        refValue={fldObj.refValue}
        type={type}
        attrName={attrName}
        label={label}
      ></ObjFieldFormula>
    );
  } else if (!value.startsWith('[')) {
    res = (
      <TextField
        key={ind + label}
        label={label}
        value={formatNumber(value)}
        unit={unit}
        fg={fg}
        bg={bg}
      ></TextField>
    );
  }
  return res;
};
//------------------------------------------------------------------------------
export const _fieldDefault = (ind, label, value, fg, bg) => {
  return (
    <TextField
      key={ind}
      label={label}
      value={value}
      fg={fg}
      bg={bg}
    ></TextField>
  );
};

//------------------------------------------------------------------------------
export const _fieldAvatar = (ind, label, value, avatarId) => {
  return (
    <AvatarField
      key={ind}
      label={label}
      value={value}
      avatarId={avatarId}
    ></AvatarField>
  );
};

//------------------------------------------------------------------------------
export const AreaField = ({ label, value }) => {
  let valueCleaned = removeTagHtml(value);
  return (
    <DivSmallTagAreaStyle title={`${label} - ${valueCleaned}`} id="small_tag">
      {SHOW_AREA_LABEL && (
        <SpanSmallTagLabelStyle> {label} </SpanSmallTagLabelStyle>
      )}
      <SpanSmallTagAreaValueStyle
        className="MarkdownMini"
        style={{ width: SHOW_AREA_LABEL ? '130px' : '100%' }}
      >
        {valueCleaned}
      </SpanSmallTagAreaValueStyle>
    </DivSmallTagAreaStyle>
  );
};
//------------------------------------------------------------------------------
export const NumberField = ({ label, value = '', unit = '', fg, bg }) => {
  return (
    <DivSmallTagNumberStyle title={label}>
      <SpanSmallTagLabelStyle> {label} </SpanSmallTagLabelStyle>

      <SpanSmallTagValueStyle $fg={fg} $bg={bg}>
        {' '}
        {value} {unit}
      </SpanSmallTagValueStyle>
    </DivSmallTagNumberStyle>
  );
};
const ProgressBarField = ({ label, value }) => {
  return (
    <DivSmallTagNumberStyle title={label}>
      <SpanSmallTagLabelStyle>
        {' '}
        {label} <ProgressBar width={value} />
      </SpanSmallTagLabelStyle>

      <div style={{ width: '100%' }}> </div>
    </DivSmallTagNumberStyle>
  );
};
export const BigNumberField = ({
  label,
  value,
  unit = '',
  nodeColor = 'var(--ds-base)',
}) => {
  return (
    <HalfBlocSmallTag>
      <HalfBlocSmallTagLabelStyle>{label}</HalfBlocSmallTagLabelStyle>
      <BigNumber
        value={value}
        unit={unit}
        nodeColor={nodeColor ?? 'var(--ds-base)'}
      />
    </HalfBlocSmallTag>
  );
};
export const BigMonetaryField = ({
  label,
  value,
  nodeColor = 'var(--ds-base)',
}) => {
  return (
    <HalfBlocSmallTag>
      <HalfBlocSmallTagLabelStyle>{label}</HalfBlocSmallTagLabelStyle>
      <BigMonetary value={value} nodeColor={nodeColor} />
    </HalfBlocSmallTag>
  );
};
export const PercentField = ({
  label,
  value,
  unit,
  nodeColor = 'var(--ds-base)',
}) => {
  return (
    <HalfBlocSmallTag>
      <HalfBlocSmallTagLabelStyle>{label}</HalfBlocSmallTagLabelStyle>

      <PercentFieldContent $nodeColor={nodeColor}>
        <MiniChartSVG val={value} size={32} /> {formatNumber(value)}
        {unit}
      </PercentFieldContent>
    </HalfBlocSmallTag>
  );
};

//------------------------------------------------------------------------------
export const AvatarField = ({ label, value, avatarId }) => {
  const { t } = useTranslation();

  return (
    <DivSmallAvatarStyle title={label}>
      <SpanSmallAvatarLabelStyle> {label} </SpanSmallAvatarLabelStyle>
      <SpanSmallAvatarValueStyle>
        {' '}
        {value} <Avatar objId={avatarId} ratio={0.6} marginRight={'0px'} />
      </SpanSmallAvatarValueStyle>
    </DivSmallAvatarStyle>
  );
};

//------------------------------------------------------------------------------
export const TextField = ({
  label,
  value,
  unit = '',
  canWrite = false,
  fg,
  bg,
}) => {
  const { t } = useTranslation();

  let isUrl =
    value?.startsWith && (value.startsWith('http') || value.startsWith('www'));
  return (
    <DivSmallTagStyle title={label}>
      <SpanSmallTagLabelStyle> {label} </SpanSmallTagLabelStyle>
      {isUrl ? (
        <SpanSmallTagValueStyle>
          <TextFieldUrlContent
            $color={'var(--ds-text-base)'}
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleLinkClick}
          >
            {simplifyUrl(value)}
          </TextFieldUrlContent>
        </SpanSmallTagValueStyle>
      ) : (
        <SpanSmallTagValueStyle $canWrite={canWrite} $fg={fg} $bg={bg}>
          {' '}
          {value}
          {unit}{' '}
        </SpanSmallTagValueStyle>
      )}
    </DivSmallTagStyle>
  );
};

const handleLinkClick = (e) => {
  e.stopPropagation(); // Empêche la propagation de l'événement
  console.log('Link clicked');
};

const simplifyUrl = (url) => {
  let cleaned = url;
  if (cleaned) {
    cleaned = url.toLowerCase();
    cleaned = cleaned.replace(/^https?:\\?\/\\?\//, '');
    // Enlever www. s'il est présent
    cleaned = cleaned.replace(/^www\./, '');
  }
  return cleaned;
};

//------------------------------------------------------------------------------
export const getObjectDefFromType = (type, objectsDefList) =>
  objectsDefList.filter((odf) => odf.key === type)[0];
